import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Download } from 'lucide-react';
import CadastroFundoLiquido from '../fundo_liquido';
import DataTable, { ColumnDef } from '../../../components/DataTable';
import DataToolbar, { ToolbarAction } from '../../../components/DataToolbar/DataToolbar';
import Pagination from '../../../components/Pagination/Pagination';
import FundoTipoSwitcher from '../../../components/FundoTipoSwitcher/FundoTipoSwitcher';
import FundoDetalhe from '../fundo_detalhe';
import { FundoListItem } from '../fundo_liquido/mock';
import { listarFundos } from '../fundo_liquido/fundosService';
import { exportarFundosXlsx } from '../fundo_liquido/exportFundos';
import { maskCnpj } from '../fundo_liquido/fields';
import './style.css';

const statusClass = (s: string): string => {
  const n = (s || '').toLowerCase();
  if (n.includes('inativo')) return 'inativo';
  if (n.includes('ativo')) return 'ativo';
  if (n.includes('capta')) return 'captacao';
  if (n.includes('encerr')) return 'encerrado';
  if (n.includes('suspens')) return 'suspenso';
  return 'default';
};

// ---- skeleton de carregamento (reusa a fiori-table e as mesmas colunas) ----
const SkBar: React.FC<{ w?: string | number; h?: number }> = ({ w = '100%', h = 12 }) => (
  <span className="fh-sk" style={{ width: typeof w === 'number' ? `${w}px` : w, height: h }} />
);

// larguras pseudo-variadas por coluna para a tabela não ficar "uniforme demais"
const SK_WIDTHS = ['70%', '92%', '85%', '55%', '45%', '80%', '72%', '68%', '62%', '50%', '50%', '55%', '55%', '40%', '45%', '50%'];

const SkeletonTabela: React.FC<{ colunas: ColumnDef<FundoListItem>[]; linhas?: number }> = ({ colunas, linhas = 10 }) => (
  <div className="table-container" aria-busy="true" aria-label="Carregando fundos">
    <table className="fiori-table">
      <thead>
        <tr>{colunas.map(c => <th key={c.key}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {Array.from({ length: linhas }).map((_, r) => (
          <tr key={r} className="table-row">
            {colunas.map((c, i) => (
              <td key={c.key}><SkBar w={SK_WIDTHS[i % SK_WIDTHS.length]} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FundosHome: React.FC = () => {
  const { tipo, fundoId } = useParams<{ tipo: string; fundoId?: string }>();
  const navigate = useNavigate();
  const base = `/cadastro/fundo/${tipo ?? 'liquidos'}`;

  const [fundos, setFundos] = useState<FundoListItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [cadastrando, setCadastrando] = useState(false);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setFundos(await listarFundos());
    } catch (e: any) {
      setErro(e?.message ?? 'Falha ao carregar os fundos.');
      setFundos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // filtro por busca (client-side sobre os fundos carregados)
  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return fundos;
    return fundos.filter(f =>
      f.nome_fantasia.toLowerCase().includes(q) ||
      f.razao_social.toLowerCase().includes(q) ||
      f.administradora.toLowerCase().includes(q) ||
      f.cnpj.includes(q.replace(/\D/g, '')) ||
      f.codigo_anbima.includes(q),
    );
  }, [busca, fundos]);

  // recorte da página atual
  const inicio = (pagina - 1) * tamanhoPagina;
  const paginaAtual = filtrados.slice(inicio, inicio + tamanhoPagina);

  const colunas = useMemo((): ColumnDef<FundoListItem>[] => [
    { key: 'cnpj',          label: 'CNPJ',          sortable: true, render: f => maskCnpj(f.cnpj) },
    { key: 'nome_fantasia', label: 'Nome Fantasia', sortable: true },
    { key: 'codigo_anbima', label: 'Código ANBIMA', sortable: true },
    { key: 'administradora', label: 'Administrador', sortable: true },
    { key: 'gestor',        label: 'Gestor',        sortable: true },
    { key: 'classe_anbima', label: 'Classe ANBIMA', sortable: true },
    { key: 'classe_anga_1', label: 'Classe ANGA 1', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: f => <span className={`fh-status fh-status--${statusClass(f.status)}`}>{f.status}</span>,
    },
    { key: 'condominio',  label: 'Condomínio',  sortable: true },
    { key: 'conta_cetip', label: 'Conta CETIP', sortable: true },
    { key: 'conta_selic', label: 'Conta SELIC', sortable: true },
  ], []);

  const acoes: ToolbarAction[] = [
    {
      key: 'export',
      label: 'Exportar',
      icon: <Download size={15} />,
      title: 'Exportar a lista (filtro atual) para Excel',
      onClick: () => { if (filtrados.length) exportarFundosXlsx(filtrados); },
    },
  ];

  // abre a tela de detalhe do fundo — o id vai pra URL.
  const abrirFundo = (fundo: FundoListItem) => navigate(`${base}/${fundo.id}`);

  // URL com id de fundo → mostra o detalhe (fonte da verdade na rota; ele busca via GET /fundos/{id}).
  if (fundoId) {
    const idNum = Number(fundoId);
    return (
      <FundoDetalhe
        fundoId={idNum}
        onVoltar={() => { navigate(base); carregar(); }}
      />
    );
  }

  if (cadastrando) {
    return (
      <div className="fh-wrap">
        <CadastroFundoLiquido
          onVoltar={() => { setCadastrando(false); carregar(); }}
          onCadastrado={(id) => { setCadastrando(false); navigate(`${base}/${id}`); }}
        />
      </div>
    );
  }

  return (
    <div className="fh-home">
      <DataToolbar
        title={<FundoTipoSwitcher />}
        searchValue={busca}
        searchPlaceholder="Buscar por nome, CNPJ, código..."
        onSearchChange={(v) => { setBusca(v); setPagina(1); }}
        actions={acoes}
        primaryLabel="Novo"
        primaryIcon={<Plus size={16} />}
        onPrimary={() => setCadastrando(true)}
      />

      {carregando ? (
        <SkeletonTabela colunas={colunas} linhas={tamanhoPagina} />
      ) : (
        <DataTable<FundoListItem>
          data={paginaAtual}
          columns={colunas}
          rowKey={(f, i) => `${f.id}-${i}`}
          onRowClick={abrirFundo}
          emptyTitle={erro ? 'Erro ao carregar' : 'Nenhum fundo encontrado'}
          emptyMessage={erro ?? 'Não há fundos com os filtros aplicados.'}
          emptyHint={erro ? 'Tente novamente em instantes.' : 'Ajuste a busca ou cadastre um novo fundo.'}
        />
      )}

      {!carregando && filtrados.length > 0 && (
        <Pagination
          currentPage={pagina - 1}
          totalPages={Math.ceil(filtrados.length / tamanhoPagina)}
          pageSize={tamanhoPagina}
          totalElements={filtrados.length}
          onPageChange={(p) => setPagina(p + 1)}
          onPageSizeChange={(s) => { setTamanhoPagina(s); setPagina(1); }}
        />
      )}
    </div>
  );
};

export default FundosHome;
